import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import ConfirmationDialog from './components/ConfirmationDialog';
import { reoptimizeScenario } from './api/tumblerApi';
import normalizeApiError from './utils/normalizeApiError';
import { bangkokLocalInputToIso, formatBangkokDateTime } from './utils/bangkokTime';

// IMPLEMENTATION_PLAN.md Phase 3 — re-optimizing a PUBLISHED scenario while production is
// running.
//
// Rules encoded here directly from the plan:
//  - The button/flow only exists on a PUBLISHED scenario.
//  - We POST once with freeze_at + planning_end and use the *returned* revision — we never call
//    solve again, since /reoptimize/ already solves.
//  - Creating the revision must not change what's currently published; only a successful
//    publish of that revision does. So this component only creates a draft revision and hands
//    it back to the parent (ScenarioDetail) to review/approve/publish through the normal
//    lifecycle actions — it does not publish anything itself.
//  - On a stale-revision / already-started-work error from the backend, we surface the server
//    error, tell the caller to refetch the current published plan, and let the planner start a
//    new revision — we never auto-retry publish.
//  - A duplicate click while a request is in flight is blocked (`busy` disables the button); if
//    the request fails from a timeout/disconnect we do NOT resubmit automatically, because the
//    backend solve is synchronous and may have already completed server-side.
export default function ReoptimizationReview({ scenario, onRevisionCreated, onRefetchPublished }) {
  const accessToken = useSelector((state) => state.auth.access_token);
  const [freezeAt, setFreezeAt] = useState('');
  const [planningEnd, setPlanningEnd] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!scenario) return null;
  if (scenario.status !== 'PUBLISHED') {
    return (
      <Typography color="text.secondary">
        Re-optimization is only available on a published scenario. This scenario is {scenario.status}.
      </Typography>
    );
  }

  const submit = async () => {
    if (busy) return; // duplicate-click guard
    if (!freezeAt || !planningEnd) {
      setMessage({ severity: 'error', text: 'Set both a freeze point and a new planning end before creating a revision.' });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const response = await reoptimizeScenario({
        scenarioId: scenario.id,
        freezeAt: bangkokLocalInputToIso(freezeAt),
        planningEnd: bangkokLocalInputToIso(planningEnd),
        accessToken,
      });
      setMessage({ severity: response.data.status === 'SOLVED' ? 'success' : 'warning', text: `Revision #${response.data.id}: ${response.data.status}. It has not been published — the currently published plan is unchanged until you approve and publish it.` });
      onRevisionCreated?.(response.data);
    } catch (error) {
      const normalized = normalizeApiError(error, 'Unable to create revision.');
      const isStale = normalized.code === 'STALE_REVISION' || /stale|already started|conflict|fresh re-optimization|no longer published/i.test(normalized.summary || '');
      setMessage({
        severity: 'error',
        text: isStale
          ? `${normalized.summary} — refreshing the current published plan. Start a new revision once you've reviewed what changed.`
          : normalized.summary,
      });
      if (isStale) {
        onRefetchPublished?.();
      }
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Creates a new revision from published scenario #{scenario.id}. Work already running or preserved before the
        freeze point is kept; only future work is replanned. This does not change the current published plan —
        review the revision, then approve and publish it separately.
      </Typography>

      {message && <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <TextField
          label="Freeze at (Bangkok)"
          type="datetime-local"
          value={freezeAt}
          onChange={(event) => setFreezeAt(event.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          helperText="Work starting before this point is preserved as-is."
        />
        <TextField
          label="New planning end (Bangkok)"
          type="datetime-local"
          value={planningEnd}
          onChange={(event) => setPlanningEnd(event.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <Button variant="contained" disabled={busy} onClick={() => setConfirmOpen(true)}>
          {busy ? 'Solving…' : 'Create revision'}
        </Button>
      </Stack>

      {Array.isArray(scenario.preserved_batches) && scenario.preserved_batches.length > 0 && (
        <Box>
          <Typography variant="subtitle2">Preserved from this plan on last revision</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
            {scenario.preserved_batches.map((batch) => (
              <Chip key={batch.id} size="small" label={`#${batch.id} ${batch.sku}`} />
            ))}
          </Stack>
        </Box>
      )}

      {scenario.change_summary && (
        <Box>
          <Typography variant="subtitle2">Last revision change summary</Typography>
          <Typography variant="body2">Added POs: {(scenario.change_summary.added_process_orders || []).join(', ') || '—'}</Typography>
          <Typography variant="body2">Cancelled POs: {(scenario.change_summary.cancelled_process_orders || []).join(', ') || '—'}</Typography>
          {Object.entries(scenario.change_summary.target_changes_raw_kg || {}).map(([sku, kg]) => <Typography key={sku} variant="body2">{sku}: {kg > 0 ? '+' : ''}{kg} kg raw</Typography>)}
        </Box>
      )}

      {scenario.reoptimized_from && (
        <Typography variant="caption" color="text.secondary">
          Reoptimized from scenario #{scenario.reoptimized_from} at {formatBangkokDateTime(scenario.freeze_at)}.
        </Typography>
      )}

      <ConfirmationDialog
        open={confirmOpen}
        title="Create a new revision?"
        description="This solves a new plan for future work only. It will not replace the published plan until you approve and publish it."
        confirmLabel="Create revision"
        busy={busy}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={submit}
      />
    </Stack>
  );
}
